import React, { createContext, useState, useContext, ReactNode, useEffect } from "react";
import { PublicClientApplication, AccountInfo, InteractionRequiredAuthError, AuthenticationResult, EventType } from "@azure/msal-browser";
import { msalConfig, authMS, authLOCAL, authRESET, getRedirectUri, loginRequest } from "./authConfig";
import { localStorageAuthUriKey, localStorageIsFrameKey } from "./lsts"

interface MsalContextType {
    msalInstance: PublicClientApplication | null;
    updateAuthority: (newAuthority: string) => Promise<void>;
    getToken: (scopes: string[]) => Promise<AuthenticationResult | null>;
}

const MsalContext = createContext<MsalContextType | undefined>(undefined);

interface MsalProviderProps {
    children: ReactNode;
}

export const MsalProvider = ({ children }: MsalProviderProps) => {
    const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);

    const setLocalUserFlowCache = (eventAuthority: string) => {
        if (eventAuthority) {
            import.meta.env.DEV === true && console.log("‹cache_auth_local›", msalConfig);

            localStorage.setItem(localStorageAuthUriKey, eventAuthority);
        }
    };

    useEffect(() => {
        const initializeMsal = async () => {
            const instance = new PublicClientApplication(msalConfig);
            await instance.initialize();

            instance?.addEventCallback(event => {

                if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && event.payload) {
                    import.meta.env.DEV === true && console.log("‹ctx‾tok_acq›", event.payload);

                    setLocalUserFlowCache((event.payload as any).authority);
                    const account = event.payload as AccountInfo;
                    instance.setActiveAccount(account);

                } else if (event.eventType === EventType.LOGIN_SUCCESS) {
                    import.meta.env.DEV === true && console.log("‹ctx‾log_ok›", event.payload);

                    setLocalUserFlowCache((event.payload as any).authority);
                    const account = event.payload as AccountInfo;
                    instance.setActiveAccount(account);

                } else if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
                    import.meta.env.DEV === true && console.log("‹ctx‾tok_errr›", event.payload);

                } else if (event.eventType === EventType.LOGOUT_END) {
                    import.meta.env.DEV === true && console.log("‹ctx‾out_ok›", event.payload);

                    localStorage.removeItem(localStorageAuthUriKey);
                    window.location.href = "/";

                } else if (event.eventType === EventType.LOGIN_FAILURE && event.error && (event.error as any).errorMessage) {
                    import.meta.env.DEV === true && console.log("‹ctx‾log_errr›", event.payload);

                    if ((event.error as any).errorMessage.includes("AADB2C90118")) {
                        if (localStorage.getItem(localStorageAuthUriKey) != null) {
                            import.meta.env.DEV === true && console.log("‹ctx‾tok_errr›", event);
                        } else {
                            import.meta.env.DEV === true && console.log("‹ctx_tok_else_errr›", event);
                        }
                    }
                } else if (event.eventType === EventType.ACQUIRE_TOKEN_NETWORK_START) {
                    import.meta.env.DEV === true && console.log("‹ctx_tok_start›", event.payload);

                    setLocalUserFlowCache((event.payload as any).authority);
                    const account = event.payload as AccountInfo;
                    instance.setActiveAccount(account);

                } else {
                    import.meta.env.DEV === true && console.log("‹ctx_else›", event);
                }
            });
            if (localStorage.getItem(localStorageIsFrameKey) !== null) {
                import.meta.env.DEV === true && console.log("‹ifrm_redir›");

                const redirectResponse = await instance.handleRedirectPromise();
                let accessToken = redirectResponse?.accessToken;
            }
            import.meta.env.DEV === true && console.log("‹set_sal›", instance);
            setMsalInstance(instance);
        };
        import.meta.env.DEV === true && console.log("‹init_sal›");

        if (!localStorage.getItem(localStorageAuthUriKey) || localStorage.getItem(localStorageAuthUriKey) == null) {
            import.meta.env.DEV === true && console.log("‹cbak_noflow›");
        } else {
            import.meta.env.DEV === true && console.log("‹cbak_flowcach›", localStorage.getItem(localStorageAuthUriKey));
        }

        initializeMsal();
    }, []);

    const updateAuthority = async (newAuthority: string) => {
        import.meta.env.DEV === true && console.log("‹up_new_aut›", newAuthority);
        if (msalInstance) {
            const newConfig = {
                ...msalConfig,
                auth: {
                    ...msalConfig.auth,
                    authority: newAuthority
                }
            };
            import.meta.env.DEV === true && console.log("‹up_new_aut_conf›", newConfig);
            const newInstance = new PublicClientApplication(newConfig);
            await newInstance.initialize();
            setMsalInstance(newInstance);
        }
    };

    const getToken = async (scopes: string[]): Promise<AuthenticationResult | null> => {
        if (!msalInstance) {
            import.meta.env.DEV === true && console.log("‹get_tok_no_msal›");
            return null;
        }
        const account = msalInstance.getActiveAccount();

        if (!account) {
            import.meta.env.DEV === true && console.log("‹get_tok_no_acc›", msalInstance, account);
            return null;
        } else {
            import.meta.env.DEV === true && console.log("‹get_tok_yes_acc›", msalInstance, account);
        }
        const request = {
            account: account,
            scopes: scopes
        };
        try {
            import.meta.env.DEV === true && console.log("‹get_tok_acq_silent_start›", msalInstance, account);
            const response = await msalInstance.acquireTokenSilent(request);
            import.meta.env.DEV === true && console.log("‹get_tok_acq_silent_end›", response);
            return response;
        } catch (e) {
            if (e instanceof InteractionRequiredAuthError) {
                import.meta.env.DEV === true && console.log("‹get_tok_err_inter_req›", e);
                return null;
            } else {
                console.error(e);
                return null;
            }
        }
    };
    return <MsalContext.Provider value={{ msalInstance, updateAuthority, getToken }}>{children}</MsalContext.Provider>;
};

export const useMsal = (): MsalContextType => {
    const context = useContext(MsalContext);
    if (!context) {
        throw new Error("useMsal must be used within a MsalProvider");
    }
    return context;
};
